/* eslint-disable max-lines */
//deps
import React, { useRef, useCallback, useMemo, useEffect, useState } from "react";
import { useKeyboardState } from "@swiggy-private/checkout-core";
import { format, isEmpty } from "@swiggy-private/js-utils";
import { SESSION_STORAGE_KEYS, Storage, useEffectOnce } from "@swiggy-private/core-ui";
import { EXPENSE_CODE_HALF_CARD_TEST_IDS } from "@swiggy-private/payment-test-ids";

//components
import { FullWidthButton } from "@swiggy-private/dls-web-core/dist/components/FullWidthButton";
import { TextField } from "@swiggy-private/dls-web-core/dist/components/TextField";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { SuggestiveTyping } from "@swiggy-private/dls-web-recipe-food-web/dist/components/SuggestiveTyping";
import { TextFieldTypes } from "@swiggy-private/dls-web-core/dist/components/TextField/types";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { default as tickFilled } from "@swiggy-private/dls-web-icons/dist/web/generic/tickFilled/20";
import OptionText from "./OptionText";
import { default as infoFilled } from "@swiggy-private/dls-web-icons/dist/web/generic/infoFilled/12";
// styles
import {
    Container,
    Title,
    DataContainer,
    CtaContainer,
    EmptyResultsText,
    Footer,
    SuccessContainer,
    IconContainer,
    TimerContainer,
    DescriptionContainer,
    InfoIconContainer,
    FieldRenderer,
} from "./styles";

// types
import { IExpenseCodeHalfcardContentProps } from "./types";

// constants
import {
    EXPENSE_CODE_HALF_CARD_TEXTS,
    FIELDS,
    SUGGEST_DROPDOWN_HEIGHT,
    CLOSE_HALFCARD_TIMER,
    TIMER_TEXT,
    SUBMIT_BUTTON_TEXT,
} from "./constants";

//helpers
import { isB2CHalfCardConfigValid } from "@payment-ui/helpers/B2Config";
import { useFieldRenderer } from "./hooks/useRendererField";
import { B2CHalfCardConfig } from "@payment-ui/interfaces/BillToCompany";

const ExpenseCodeHalfcardContent = ({
    fieldValue,
    projectCodeValues,
    requiredFields,
    handleInputChange,
    onSubmit,
    optionValue,
    setOptionValue,
    isProjectCodeSelected,
    setIsProjectCodeSelected,
    submitButtonText,
    showDescription,
    setIsDetailsSubmitted,
    isRedirectToPlaceOrder,
    isDetailsSubmitted,
    analytics,
    expenseCodeDataInCache,
    b2CHalfCardConfig,
    apiCallHandlers,
}: IExpenseCodeHalfcardContentProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [emptyResultsContent, setEmptyResultsContent] = useState<React.ReactNode>(null);
    const [timer, setTimer] = useState(CLOSE_HALFCARD_TIMER);
    const b2CHalfCardConfigValid = isB2CHalfCardConfigValid(b2CHalfCardConfig);
    useKeyboardState(containerRef);
    const changeHandler = useCallback(
        (fieldName: string, inputString: string) => {
            handleInputChange(fieldName, inputString);
        },
        [handleInputChange],
    );
    const onClickHandler = useCallback(
        ({ suggestion }: Record<string, unknown> = {}) => {
            changeHandler(FIELDS.PROJECT_CODE, suggestion as string);
            setOptionValue([]);
            setIsProjectCodeSelected(true);
        },
        [changeHandler, setOptionValue, setIsProjectCodeSelected],
    );
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const handleSubmit = useCallback(() => {
        const data: Record<string, string> = {};
        if (fieldValue[FIELDS.PROJECT_CODE]) {
            data.projectCode = fieldValue[FIELDS.PROJECT_CODE];
        }
        if (fieldValue[FIELDS.PURPOSE_OF_ORDERING]) {
            data.purposeOfOrdering = fieldValue[FIELDS.PURPOSE_OF_ORDERING];
        }
        Storage.setItem(SESSION_STORAGE_KEYS.EXPENSE_CODE_DATA, Object.keys(data).length > 0 ? data : null);
        analytics?.onSubmitClick();
        setIsDetailsSubmitted(true);
        if (isRedirectToPlaceOrder) {
            setTimeout(() => {
                onSubmit();
            }, CLOSE_HALFCARD_TIMER * 1000);
        } else {
            onSubmit();
        }
    }, [onSubmit, fieldValue, isRedirectToPlaceOrder, setIsDetailsSubmitted, analytics]);

    const options = useMemo(() => {
        const currentValue = fieldValue[FIELDS.PROJECT_CODE]?.toLowerCase();

        const getRelevanceScore = (str: string) => {
            const lower = str.toLowerCase();
            if (lower === currentValue) return 3; // Exact match
            if (lower.startsWith(currentValue)) return 2; // Starts with
            if (lower.includes(currentValue)) return 1; // Contains
            return 0; // No match
        };

        // Create array of options with their scores
        const optionsWithScores = optionValue.map((option) => ({
            value: option,
            score: getRelevanceScore(option),
        }));

        // Sort by score, then alphabetically
        const sortedOptionsWithScores = optionsWithScores.sort((a, b) => {
            return b.score - a.score || a.value.toLowerCase().localeCompare(b.value.toLowerCase());
        });

        // Optional: Filter out options with score 0 (no matches)
        const filteredOptionsWithScores = sortedOptionsWithScores.filter((item) => item.score > 0);

        // Extract just the values for backward compatibility
        const sortedOptions = filteredOptionsWithScores.map((item) => item.value);

        if (isEmpty(sortedOptions) && !isEmpty(optionValue)) {
            setEmptyResultsContent(
                <EmptyResultsText
                    variant="V2.body1.neutral.medium"
                    color="text_High_Emphasis"
                    data-testid={EXPENSE_CODE_HALF_CARD_TEST_IDS.NO_RESULTS_MESSAGE}
                >
                    {EXPENSE_CODE_HALF_CARD_TEXTS.NO_RESULT_FOUND}
                </EmptyResultsText>,
            );
        } else {
            setEmptyResultsContent(null);
        }

        return sortedOptions.map((curValue) => ({
            text: <OptionText matchStr={fieldValue[FIELDS.PROJECT_CODE]} fullStr={curValue} />,
            id: curValue,
            data: { suggestion: curValue },
        }));
    }, [fieldValue[FIELDS.PROJECT_CODE], optionValue]);

    const { renderField, getSubmitData, isFormValid } = useFieldRenderer(
        b2CHalfCardConfig as B2CHalfCardConfig,
        apiCallHandlers,
        isDetailsSubmitted,
        expenseCodeDataInCache,
    );

    const isButtonDisabled = useMemo(() => {
        // For new generic config, use isFormValid from backend's required flags
        if (b2CHalfCardConfigValid) {
            return !isFormValid;
        }
        // For old config, use legacy validation
        return requiredFields[FIELDS.PROJECT_CODE] && !isProjectCodeSelected;
    }, [requiredFields, isProjectCodeSelected, b2CHalfCardConfigValid, isFormValid]);

    useEffect(() => {
        const inputElement = inputRef?.current;
        if (!inputElement) return;

        const handleClick = () => setOptionValue(projectCodeValues);
        const handleBlur = () => {
            if (isEmpty(options)) {
                changeHandler(FIELDS.PROJECT_CODE, "");
                setOptionValue([]);
            }

            setEmptyResultsContent(null);

            setTimeout(() => {
                setOptionValue([]);
            }, 0);
        };

        inputElement.addEventListener("click", handleClick);
        inputElement.addEventListener("blur", handleBlur);

        return () => {
            inputElement.removeEventListener("click", handleClick);
            inputElement.removeEventListener("blur", handleBlur);
        };
    }, [projectCodeValues, setOptionValue, changeHandler, options]);

    useEffect(() => {
        if (isDetailsSubmitted && isRedirectToPlaceOrder && !isEmpty(timer)) {
            intervalRef.current = setInterval(() => {
                setTimer((prevTimer) => prevTimer - 1);
            }, 1000);

            return () => {
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
            };
        }
    }, [isDetailsSubmitted, isRedirectToPlaceOrder, timer]);

    useEffect(() => {
        // it will set submit button as enabled if user alredy submitted data before in same session
        setIsProjectCodeSelected(!isEmpty(expenseCodeDataInCache));
    }, [expenseCodeDataInCache]);

    useEffectOnce(() => {
        analytics?.onLoadImpression();
    });

    const handleGenericSubmit = useCallback(() => {
        const data = getSubmitData();
        const hasMeaningfulData =
            !isEmpty(data.projectCode) ||
            !isEmpty(data.purposeOfOrdering) ||
            !isEmpty(data.tripTags) ||
            !isEmpty(data.attendeeEmail) ||
            !isEmpty(data.groupOrderDetails);

        Storage.setItem(SESSION_STORAGE_KEYS.EXPENSE_CODE_DATA, hasMeaningfulData ? data : null);

        analytics?.onSubmitClick();
        setIsDetailsSubmitted(true);

        if (isRedirectToPlaceOrder) {
            setTimeout(() => {
                onSubmit();
            }, CLOSE_HALFCARD_TIMER * 1000);
        } else {
            onSubmit();
        }
    }, [getSubmitData, isRedirectToPlaceOrder, setIsDetailsSubmitted, analytics, onSubmit]);

    return (
        <Container ref={containerRef} data-testid={EXPENSE_CODE_HALF_CARD_TEST_IDS.CONTAINER}>
            <DataContainer data-testid={EXPENSE_CODE_HALF_CARD_TEST_IDS.DATA_CONTAINER}>
                <Title
                    variant="V2.heading2.neutral.bold"
                    color="text_Highest_Emphasis"
                    data-testid={EXPENSE_CODE_HALF_CARD_TEST_IDS.TITLE}
                >
                    {b2CHalfCardConfig?.title || EXPENSE_CODE_HALF_CARD_TEXTS.TITLE}
                </Title>

                <FieldRenderer>
                    {b2CHalfCardConfigValid ? (
                        <>{renderField}</>
                    ) : (
                        <>
                            <SuggestiveTyping
                                value={fieldValue[FIELDS.PROJECT_CODE]}
                                name={FIELDS.PROJECT_CODE}
                                options={options}
                                label={
                                    requiredFields[FIELDS.PROJECT_CODE]
                                        ? EXPENSE_CODE_HALF_CARD_TEXTS.PROJECT_CODE_LABEL
                                        : EXPENSE_CODE_HALF_CARD_TEXTS.PROJECT_CODE_LABEL_OPTIONAL
                                }
                                onChangeValue={(value: string) => {
                                    changeHandler(FIELDS.PROJECT_CODE, value);
                                    setIsProjectCodeSelected(false);
                                }}
                                onClick={onClickHandler}
                                emptyResultsContent={emptyResultsContent}
                                inputRef={inputRef}
                                containerHeight={!isEmpty(options) ? SUGGEST_DROPDOWN_HEIGHT : undefined}
                                attrs={{
                                    disabled: isDetailsSubmitted,
                                    autoComplete: "off",
                                    "data-testid": EXPENSE_CODE_HALF_CARD_TEST_IDS.PROJECT_CODE_INPUT,
                                }}
                            />

                            <TextField
                                textFieldDetails={{
                                    value: fieldValue[FIELDS.PURPOSE_OF_ORDERING],
                                    name: FIELDS.PURPOSE_OF_ORDERING,
                                    label: EXPENSE_CODE_HALF_CARD_TEXTS.PURPOSE_OF_ORDERING_LABEL,
                                    onChange: (value: string) => changeHandler(FIELDS.PURPOSE_OF_ORDERING, value),
                                    type: TextFieldTypes.TEXT,
                                    attrs: {
                                        disabled: isDetailsSubmitted,
                                        autoComplete: "off",
                                        "data-testid": EXPENSE_CODE_HALF_CARD_TEST_IDS.PURPOSE_INPUT,
                                    },
                                }}
                            />
                        </>
                    )}
                </FieldRenderer>
            </DataContainer>
            <Footer data-testid={EXPENSE_CODE_HALF_CARD_TEST_IDS.FOOTER}>
                <CtaContainer data-testid={EXPENSE_CODE_HALF_CARD_TEST_IDS.CTA_CONTAINER}>
                    <FullWidthButton
                        variant="tertiary"
                        onClick={b2CHalfCardConfigValid ? handleGenericSubmit : handleSubmit}
                        isDisabled={isButtonDisabled || isDetailsSubmitted}
                        data-testid={EXPENSE_CODE_HALF_CARD_TEST_IDS.SUBMIT_BUTTON}
                    >
                        {submitButtonText || SUBMIT_BUTTON_TEXT.DEFAULT}
                    </FullWidthButton>
                </CtaContainer>
                {isRedirectToPlaceOrder && isDetailsSubmitted && (
                    <>
                        <SuccessContainer data-testid={EXPENSE_CODE_HALF_CARD_TEST_IDS.SUCCESS_CONTAINER}>
                            <IconContainer data-testid={EXPENSE_CODE_HALF_CARD_TEST_IDS.SUCCESS_ICON}>
                                <GenericIcon Icon={tickFilled} strokeColor="positive" fillColor="positive" />
                            </IconContainer>
                            <Text
                                variant="V2.body3.neutral.medium"
                                color="positive"
                                data-testid={EXPENSE_CODE_HALF_CARD_TEST_IDS.SUCCESS_TEXT}
                            >
                                {EXPENSE_CODE_HALF_CARD_TEXTS.BUSINESS_DETAILS_SUBMITTED}
                            </Text>
                        </SuccessContainer>
                        <TimerContainer data-testid={EXPENSE_CODE_HALF_CARD_TEST_IDS.TIMER_CONTAINER}>
                            <Text
                                variant="V2.body2.neutral.medium"
                                color="text_Med_Emphasis"
                                data-testid={EXPENSE_CODE_HALF_CARD_TEST_IDS.TIMER_TEXT}
                            >
                                {format(TIMER_TEXT, timer)}
                            </Text>
                        </TimerContainer>
                    </>
                )}
                {(!isEmpty(b2CHalfCardConfig?.showConsent) || showDescription) && (
                    <DescriptionContainer>
                        {!isEmpty(b2CHalfCardConfig?.showConsent) && (
                            <>
                                <InfoIconContainer>
                                    <GenericIcon
                                        Icon={infoFilled}
                                        strokeColor="text_Med_Emphasis"
                                        fillColor="text_Med_Emphasis"
                                    />
                                </InfoIconContainer>
                                <Text
                                    variant="V2.overline.neutral.regular"
                                    color="text_Med_Emphasis"
                                    data-testid={EXPENSE_CODE_HALF_CARD_TEST_IDS.DESCRIPTION_TEXT}
                                >
                                    {b2CHalfCardConfig?.showConsent}
                                </Text>
                            </>
                        )}
                        {showDescription && isEmpty(b2CHalfCardConfig?.showConsent) && (
                            <Text
                                variant="V2.overline.neutral.regular"
                                color="text_Med_Emphasis"
                                data-testid={EXPENSE_CODE_HALF_CARD_TEST_IDS.DESCRIPTION_TEXT}
                            >
                                {EXPENSE_CODE_HALF_CARD_TEXTS.DESCRIPTION}
                            </Text>
                        )}
                    </DescriptionContainer>
                )}
            </Footer>
        </Container>
    );
};
export default React.memo(ExpenseCodeHalfcardContent);
